import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import type {
  EbLedgerOption, EbRelationOption, InboundMail, InboundPendingSender, InboundSender, InboundSenderResult, InboundStatus,
} from '@aviaconsole/shared';
import { capi, companyUrl } from '../api/client';
import { useI18n, type Key } from '../i18n';
import { useToast } from '../components/Toast';
import { formatDate } from '../lib/format';
import { Button, Card, Chip, Empty, Input, Loading, Modal, PageHeader, Select } from '../components/ui';

const inboundTone = (s: string) => ({ processed: 'success', duplicate: 'neutral', no_attachment: 'warning', failed: 'danger', held: 'warning', blocked: 'danger' } as Record<string, string>)[s] ?? 'neutral';

/**
 * The invoice mailbox: who waits for approval, the allowlist and blocklist,
 * and everything received. Verified mail from an allowed sender is processed
 * without anyone here; this page is for the rest.
 */
export function InvoiceMailbox() {
  const { t } = useI18n();
  const toast = useToast();
  const status = useQuery({ queryKey: ['inbound-status'], queryFn: () => capi<InboundStatus>('/inbound') });
  const copy = (address: string) => navigator.clipboard?.writeText(address).then(() => toast.push(t('inMailCopied')), () => {});
  const st = status.data;
  return (
    <>
      <PageHeader crumb={t('dossierCrumb')} title={t('mbTitle')} sub={t('mbSub')} />
      <div className="stack">
        {st && (
          <div className={`banner ${st.enabled ? 'tint-accent' : 'tint-warning'} row`}>
            <i className={st.enabled ? 'fas fa-envelope-open-text' : 'fas fa-power-off'} aria-hidden="true" />
            <span style={{ flex: 1 }}>{st.enabled ? t('mbAddress', { address: st.address ?? '' }) : t('inMailOff')}</span>
            {st.enabled && st.address && <Button size="sm" icon="fas fa-copy" onClick={() => copy(st.address!)}>{t('inMailCopy')}</Button>}
          </div>
        )}
        <Pending />
        <Senders />
        <Received />
      </div>
    </>
  );
}

/** What a rule form edits: a new rule for a waiting sender, or an existing rule. */
type RuleDraft = { mode: 'new'; status: 'allowed' | 'blocked'; address: string } | { mode: 'edit'; rule: InboundSender };

function useInvalidateMailbox() {
  const qc = useQueryClient();
  return () => {
    for (const k of ['inbound-pending', 'inbound-senders', 'inbound-mails', 'purchase-invoices']) qc.invalidateQueries({ queryKey: [k] });
  };
}

// ---- waiting for approval ------------------------------------------------------------------

function Pending() {
  const { t, lang } = useI18n();
  const list = useQuery({ queryKey: ['inbound-pending'], queryFn: () => capi<InboundPendingSender[]>('/inbound/pending'), refetchInterval: 60_000 });
  const [draft, setDraft] = useState<RuleDraft | null>(null);
  return (
    <>
    <Card pad={false} className="table">
      <div className="cardhead"><h2>{t('mbPending')}</h2>{list.data && list.data.length > 0 && <Chip tone="warning">{list.data.length}</Chip>}</div>
      {!list.data ? <Loading error={list.error} /> : (
        <table className="small"><tbody>
          {list.data.length === 0 && <Empty>{t('mbPendingEmpty')}</Empty>}
          {list.data.map((p) => (
            <tr key={p.address}>
              <td style={{ verticalAlign: 'top' }}>
                <div style={{ fontWeight: 600 }}>{p.name ?? p.address}</div>
                <div className="sub">{p.address}</div>
                <div className="row" style={{ gap: '0.3rem', marginTop: '0.3rem' }}>
                  <AuthChip verified={p.verified} />
                  {p.allowedButUnverified && <Chip tone="danger" icon="fas fa-triangle-exclamation">{t('mbAllowedUnverified')}</Chip>}
                </div>
              </td>
              <td style={{ verticalAlign: 'top' }}>
                {p.mails.slice(0, 3).map((m) => (
                  <div key={m.id}>
                    <span>{m.subject || '—'}</span>
                    <span className="muted"> · {formatDate(m.receivedAt, lang)} · {m.attachments.filter((a) => a.outcome === 'waiting').map((a) => a.filename).join(', ')}</span>
                  </div>
                ))}
                {p.count > 3 && <div className="sub">{t('mbMore', { n: p.count - 3 })}</div>}
              </td>
              <td className="r" style={{ verticalAlign: 'top', whiteSpace: 'nowrap' }}>
                <Button size="sm" variant="primary" icon="fas fa-check" onClick={() => setDraft({ mode: 'new', status: 'allowed', address: p.address })}>{t('mbApprove')}</Button>{' '}
                <Button size="sm" variant="danger" icon="fas fa-ban" onClick={() => setDraft({ mode: 'new', status: 'blocked', address: p.address })}>{t('mbBlock')}</Button>
              </td>
            </tr>
          ))}
        </tbody></table>
      )}
    </Card>
    {/* Outside the card: its backdrop filter would clip a fixed-position dialog. */}
    {draft && <RuleForm draft={draft} onClose={() => setDraft(null)} />}
    </>
  );
}

function AuthChip({ verified }: { verified: boolean }) {
  const { t } = useI18n();
  return verified
    ? <Chip tone="success" icon="fas fa-shield-halved">{t('mbVerified')}</Chip>
    : <Chip tone="neutral" icon="fas fa-circle-question">{t('mbUnverified')}</Chip>;
}

// ---- the allowlist and blocklist -----------------------------------------------------------------

function useBookingOptions() {
  const ledgers = useQuery({ queryKey: ['eb-ledgers'], queryFn: () => capi<EbLedgerOption[]>('/purchase-invoices/ledgers'), retry: false, staleTime: 300_000 });
  const relations = useQuery({ queryKey: ['eb-relations'], queryFn: () => capi<EbRelationOption[]>('/purchase-invoices/relations'), retry: false, staleTime: 300_000 });
  return { ledgers, relations };
}

function Senders() {
  const { t, lang } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidateMailbox();
  const list = useQuery({ queryKey: ['inbound-senders'], queryFn: () => capi<InboundSender[]>('/inbound/senders') });
  const { ledgers, relations } = useBookingOptions();
  const [draft, setDraft] = useState<RuleDraft | null>(null);
  const remove = useMutation({
    mutationFn: (id: string) => capi(`/inbound/senders/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate, onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const ledgerName = (id: number | null) => {
    const l = id ? ledgers.data?.find((x) => x.id === id) : undefined;
    return l ? `${l.code} ${l.description}` : id ? `#${id}` : null;
  };
  const relationName = (id: number | null) => (id ? relations.data?.find((r) => r.id === id)?.name ?? `#${id}` : null);
  return (
    <>
    <Card pad={false} className="table">
      <div className="cardhead"><h2>{t('mbSenders')}</h2></div>
      {!list.data ? <Loading error={list.error} /> : (
        <table className="small">
          <thead><tr><th>{t('mbSender')}</th><th>{t('colStatus')}</th><th>{t('mbBooking')}</th><th className="r">{t('mbMails')}</th><th /></tr></thead>
          <tbody>
            {list.data.length === 0 && <Empty>{t('mbSendersEmpty')}</Empty>}
            {list.data.map((r) => (
              <tr key={r.id}>
                <td><div style={{ fontWeight: 600 }}>{r.pattern}</div><div className="sub">{t(r.kind === 'domain' ? 'mbWholeDomain' : 'mbOneAddress')}{r.note ? ` · ${r.note}` : ''}</div></td>
                <td><Chip tone={r.status === 'allowed' ? 'success' : 'danger'}>{t(r.status === 'allowed' ? 'mbAllowed' : 'mbBlocked')}</Chip></td>
                <td>{r.status === 'blocked' ? '—' : r.ebLedgerId
                  ? <><div>{t('mbBooksOn', { ledger: ledgerName(r.ebLedgerId) ?? '' })}</div>{r.ebRelationId && <div className="sub">{relationName(r.ebRelationId)}</div>}</>
                  : <span className="muted">{t('mbArchiveOnly')}</span>}</td>
                <td className="r">{r.mails}{r.lastMailAt && <div className="sub">{formatDate(r.lastMailAt, lang)}</div>}</td>
                <td className="r"><span className="row" style={{ justifyContent: 'flex-end' }}>
                  <button type="button" className="linkbtn" onClick={() => setDraft({ mode: 'edit', rule: r })}>{t('orEdit')}</button>
                  <button type="button" className="linkbtn" disabled={remove.isPending} onClick={() => remove.mutate(r.id)}>{t('mbRemove')}</button>
                </span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
    {draft && <RuleForm draft={draft} onClose={() => setDraft(null)} />}
    </>
  );
}

/** Approve, block or edit: the address or its whole domain, and for an allowed sender the ledger and supplier to book on. */
function RuleForm({ draft, onClose }: { draft: RuleDraft; onClose: () => void }) {
  const { t } = useI18n();
  const toast = useToast();
  const invalidate = useInvalidateMailbox();
  const { ledgers, relations } = useBookingOptions();
  const address = draft.mode === 'new' ? draft.address : null;
  const domain = address ? address.slice(address.lastIndexOf('@') + 1) : null;
  const [f, setF] = useState({
    pattern: draft.mode === 'new' ? draft.address : draft.rule.pattern,
    status: draft.mode === 'new' ? draft.status : draft.rule.status,
    ledger: draft.mode === 'edit' && draft.rule.ebLedgerId ? String(draft.rule.ebLedgerId) : '',
    relation: draft.mode === 'edit' && draft.rule.ebRelationId ? String(draft.rule.ebRelationId) : '',
    note: draft.mode === 'edit' ? draft.rule.note ?? '' : '',
  });
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const allowed = f.status === 'allowed';
  const ebAvailable = !!ledgers.data;
  const body = {
    status: f.status,
    ebLedgerId: allowed && f.ledger ? Number(f.ledger) : null,
    ebRelationId: allowed && f.relation ? Number(f.relation) : null,
    note: f.note.trim() || null,
  };
  const save = useMutation({
    mutationFn: () => draft.mode === 'new'
      ? capi<InboundSenderResult>('/inbound/senders', { method: 'POST', json: { pattern: f.pattern, ...body } })
      : capi<InboundSenderResult>(`/inbound/senders/${draft.rule.id}`, { method: 'PATCH', json: body }),
    onSuccess: (r) => {
      invalidate();
      toast.push(r.applied.length ? t(r.sender.status === 'allowed' ? 'mbAppliedAllowed' : 'mbAppliedBlocked', { n: r.applied.length }) : t('done'));
      onClose();
    },
    onError: (e: Error) => toast.push(e.message, 'error'),
  });
  const title = draft.mode === 'edit' ? draft.rule.pattern : t(allowed ? 'mbApproveTitle' : 'mbBlockTitle', { address: address ?? '' });
  return (
    <Modal title={title} onClose={onClose}>
      <form className="stack md" onSubmit={(e) => { e.preventDefault(); save.mutate(); }}>
        {draft.mode === 'new' && domain && (
          <Select label={t('mbScope')} value={f.pattern} onChange={set('pattern')} options={[
            { value: draft.address, label: t('mbScopeAddress', { address: draft.address }) },
            { value: domain, label: t('mbScopeDomain', { domain }) },
          ]} />
        )}
        {draft.mode === 'edit' && (
          <Select label={t('colStatus')} value={f.status} onChange={set('status')} options={[{ value: 'allowed', label: t('mbAllowed') }, { value: 'blocked', label: t('mbBlocked') }]} />
        )}
        {allowed && (
          <>
            <div className="grid fields">
              <Select label={t('mbLedger')} value={f.ledger} onChange={set('ledger')} disabled={!ebAvailable}
                options={[{ value: '', label: t('mbArchiveOnly') }, ...(ledgers.data ?? []).map((l) => ({ value: String(l.id), label: `${l.code} ${l.description}` }))]} />
              <Select label={t('mbSupplier')} value={f.relation} onChange={set('relation')} disabled={!relations.data}
                options={[{ value: '', label: t('mbSupplierRecognised') }, ...(relations.data ?? []).map((r) => ({ value: String(r.id), label: r.code ? `${r.name} (${r.code})` : r.name }))]} />
            </div>
            <div className="small muted">{ebAvailable ? t(f.ledger ? 'mbBookHint' : 'mbArchiveHint') : t('mbNoEb')}</div>
          </>
        )}
        {!allowed && <div className="small muted">{t('mbBlockHint')}</div>}
        <Input label={t('mbNote')} value={f.note} onChange={set('note')} maxLength={300} />
        <div className="row" style={{ justifyContent: 'flex-end' }}>
          <Button onClick={onClose}>{t('cancel')}</Button>
          <Button variant={allowed ? 'primary' : 'danger'} type="submit" disabled={save.isPending}>
            {draft.mode === 'edit' ? t('crmSave') : t(allowed ? 'mbApprove' : 'mbBlock')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

// ---- everything received ----------------------------------------------------------------------

function Received() {
  const { t, lang } = useI18n();
  const navigate = useNavigate();
  const toast = useToast();
  const invalidate = useInvalidateMailbox();
  const list = useQuery({ queryKey: ['inbound-mails'], queryFn: () => capi<InboundMail[]>('/inbound/mails?limit=100'), refetchInterval: 60_000 });
  const reprocess = useMutation({
    mutationFn: (id: string) => capi<InboundMail>(`/inbound/mails/${id}/reprocess`, { method: 'POST' }),
    onSuccess: invalidate, onError: (e: Error) => toast.push(e.message, 'error'),
  });
  return (
    <Card pad={false} className="table">
      <div className="cardhead"><h2>{t('inMailTitle')}</h2></div>
      {!list.data ? <Loading error={list.error} /> : (
        <table className="small">
          <thead><tr><th>{t('colIssued')}</th><th>{t('inMailFrom')}</th><th>PDF</th><th>{t('colStatus')}</th><th /></tr></thead>
          <tbody>
            {list.data.length === 0 && <Empty>{t('inMailEmpty')}</Empty>}
            {list.data.map((m) => (
              <tr key={m.id}>
                <td className="secondary" style={{ whiteSpace: 'nowrap' }}>{formatDate(m.receivedAt, lang)}</td>
                <td>
                  <div style={{ fontWeight: 600 }}>{m.fromName ?? m.fromAddress}{m.auth && <> <AuthChip verified={m.auth.verified} /></>}</div>
                  <div className="sub">{m.subject || '—'}</div>
                </td>
                <td>{m.attachments.map((a) => (
                  <div key={a.filename}>
                    {a.purchaseInvoiceId
                      ? <button type="button" className="linkbtn" onClick={() => navigate(`/dossier/${a.purchaseInvoiceId}`)}>{a.filename}</button>
                      : <span>{a.filename}</span>}
                    <span className="muted"> · {t(`inOutcome_${a.outcome}` as Key)}</span>
                    {a.detail && (a.outcome === 'booked' || a.outcome === 'archived') && <div className="sub">{a.detail}</div>}
                  </div>
                ))}</td>
                <td><Chip tone={inboundTone(m.status)}>{t(`inStatus_${m.status}` as Key)}</Chip>{m.detail && <div className="sub">{m.detail}</div>}</td>
                <td className="r"><span className="row" style={{ justifyContent: 'flex-end' }}>
                  {m.status === 'failed' && <button type="button" className="linkbtn" disabled={reprocess.isPending} onClick={() => reprocess.mutate(m.id)}>{t('inMailReprocess')}</button>}
                  <button type="button" className="linkbtn" onClick={() => window.open(companyUrl(`/inbound/mails/${m.id}/raw`), '_blank')}>{t('inMailOriginal')}</button>
                </span></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}
